/**
 * PDF Upload Hook
 * Handles uploading PDFs to Supabase Storage with progress tracking
 */

import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface PDFUploadState {
  status: 'idle' | 'uploading' | 'completed' | 'error';
  progress: number;
  error?: string;
  storagePath?: string;
  publicUrl?: string;
}

interface UsePDFUploadOptions {
  userId: string;
  journalId: string;
  blockId: string;
  onComplete?: (storagePath: string, publicUrl: string) => void;
  onError?: (error: string) => void;
}

const BUCKET_NAME = 'original-pdfs';

export function usePDFUpload({ 
  userId, 
  journalId, 
  blockId, 
  onComplete, 
  onError 
}: UsePDFUploadOptions) {
  const [state, setState] = useState<PDFUploadState>({
    status: 'idle',
    progress: 0,
  });
  
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const upload = useCallback(async (file: File) => {
    // Generate storage path
    const fileExt = file.name.split('.').pop() || 'pdf';
    const storagePath = `${userId}/${journalId}/${blockId}.${fileExt}`;
    
    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();
    
    setState({
      status: 'uploading',
      progress: 0,
    });
    
    try {
      // Upload to Supabase Storage
      // Note: Supabase doesn't support progress events natively
      // We simulate progress or use XMLHttpRequest for real progress
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: true, // Allow overwriting
        });
      
      if (error) {
        throw error;
      }
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(storagePath);
      
      const publicUrl = urlData.publicUrl;
      
      setState({
        status: 'completed',
        progress: 100,
        storagePath,
        publicUrl,
      });
      
      onComplete?.(storagePath, publicUrl);
      
      return { storagePath, publicUrl };
    } catch (error: any) {
      // Check if aborted
      if (error.name === 'AbortError') {
        setState({
          status: 'idle',
          progress: 0,
        });
        return null;
      }
      
      const errorMessage = error.message || 'Upload failed';
      
      setState({
        status: 'error',
        progress: 0,
        error: errorMessage,
      });
      
      onError?.(errorMessage);
      toast.error(`PDF upload failed: ${errorMessage}`);
      
      return null;
    }
  }, [userId, journalId, blockId, onComplete, onError]);
  
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setState({
      status: 'idle',
      progress: 0,
    });
  }, []);
  
  const reset = useCallback(() => {
    setState({
      status: 'idle',
      progress: 0,
    });
  }, []);
  
  return {
    ...state,
    upload,
    cancel,
    reset,
    isUploading: state.status === 'uploading',
  };
}

/**
 * Delete a PDF from Supabase Storage
 */
export async function deletePDFFromStorage(storagePath: string): Promise<boolean> {
  try {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([storagePath]);
    
    if (error) {
      console.error('Error deleting PDF from storage:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting PDF from storage:', error);
    return false;
  }
}

/**
 * Get a signed URL for a PDF (for private buckets)
 * For public buckets, use getPublicUrl instead
 */
export async function getPDFSignedUrl(storagePath: string): Promise<string | null> {
  try {
    const { data } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(storagePath);
    
    return data.publicUrl;
  } catch (error) {
    console.error('Error getting PDF URL:', error);
    return null;
  }
}

